import React from 'react';

export interface NeraTagFilterRowProps {
  suggestedTags: string[];
  emptySuggestionsLabel?: string;
  includeGroups: string[][];
  activeIncludeGroupIndex: number;
  setActiveIncludeGroupIndex: (index: number) => void;
  includeTagInput: string;
  setIncludeTagInput: (v: string) => void;
  addIncludeTagToGroup: (groupIndex: number, tag: string) => void;
  removeIncludeTagFromGroup: (groupIndex: number, tag: string) => void;
  addOrGroup: () => void;
  includeSuggestions: string[];
  excludeTags: string[];
  excludeTagInput: string;
  setExcludeTagInput: (v: string) => void;
  addExcludeTag: (tag: string) => void;
  removeExcludeTag: (tag: string) => void;
  excludeSuggestions: string[];
}

const NeraTagFilterRow: React.FC<NeraTagFilterRowProps> = ({
  suggestedTags,
  emptySuggestionsLabel = 'No tags or metrics on this date',
  includeGroups,
  activeIncludeGroupIndex,
  setActiveIncludeGroupIndex,
  includeTagInput,
  setIncludeTagInput,
  addIncludeTagToGroup,
  removeIncludeTagFromGroup,
  addOrGroup,
  includeSuggestions,
  excludeTags,
  excludeTagInput,
  setExcludeTagInput,
  addExcludeTag,
  removeExcludeTag,
  excludeSuggestions,
}) => {
  const activeIndex = Math.min(activeIncludeGroupIndex, Math.max(0, includeGroups.length - 1));

  return (
    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '1.5rem', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', minWidth: 0, maxWidth: '480px', flex: '1 1 320px' }}>
        <div>
          <span style={{ fontSize: '0.85rem', color: '#555', fontWeight: 'bold' }}>Include tags / metrics</span>
          <span style={{ fontSize: '0.75rem', color: '#888', marginLeft: '0.5rem' }}>
            Groups are OR; tokens within a group are AND
          </span>
        </div>

        {includeGroups.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {groupIndex > 0 && (
              <div style={{ display: 'flex', justifyContent: 'center', margin: '0.15rem 0' }}>
                <span
                  style={{
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    color: '#6c757d',
                    background: '#e9ecef',
                    padding: '0.15rem 0.6rem',
                    borderRadius: '999px',
                  }}
                >
                  OR
                </span>
              </div>
            )}
            <div
              role="button"
              tabIndex={0}
              onClick={() => setActiveIncludeGroupIndex(groupIndex)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setActiveIncludeGroupIndex(groupIndex);
                }
              }}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '0.35rem',
                padding: '0.4rem',
                border:
                  groupIndex === activeIndex ? '2px solid #0d6efd' : '1px solid #dee2e6',
                borderRadius: '4px',
                background: groupIndex === activeIndex ? '#f8fbff' : '#fff',
                cursor: 'pointer',
              }}
            >
              {group.map((tag, tagIndex) => (
                <React.Fragment key={tag}>
                  {tagIndex > 0 && (
                    <span style={{ fontSize: '0.7rem', color: '#888', fontWeight: 'bold' }}>and</span>
                  )}
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.2rem',
                      padding: '0.2rem 0.4rem',
                      fontSize: '0.85rem',
                      background: '#e7f1ff',
                      border: '1px solid #b6d4fe',
                      borderRadius: '4px',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeIncludeTagFromGroup(groupIndex, tag)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        lineHeight: 1,
                        color: '#666',
                      }}
                      aria-label={`Remove ${tag}`}
                    >
                      ×
                    </button>
                  </span>
                </React.Fragment>
              ))}
              <input
                type="text"
                value={groupIndex === activeIndex ? includeTagInput : ''}
                onChange={(e) => {
                  setActiveIncludeGroupIndex(groupIndex);
                  setIncludeTagInput(e.target.value);
                }}
                onFocus={() => setActiveIncludeGroupIndex(groupIndex)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    addIncludeTagToGroup(groupIndex, includeTagInput);
                  }
                }}
                placeholder="Add tag or metric…"
                style={{
                  border: 'none',
                  outline: 'none',
                  padding: '0.2rem',
                  fontSize: '0.9rem',
                  minWidth: '80px',
                  flex: '1 1 80px',
                  background: 'transparent',
                }}
              />
            </div>
          </React.Fragment>
        ))}

        <button
          type="button"
          onClick={addOrGroup}
          style={{
            alignSelf: 'flex-start',
            padding: '0.25rem 0.6rem',
            fontSize: '0.8rem',
            border: '1px solid #b6d4fe',
            borderRadius: '4px',
            background: '#fff',
            color: '#0d6efd',
            cursor: 'pointer',
          }}
        >
          + OR group
        </button>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.2rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: '#666', marginRight: '0.3rem' }}>Suggestions:</span>
          {suggestedTags.length === 0 ? (
            <span style={{ fontSize: '0.8rem', color: '#999' }}>{emptySuggestionsLabel}</span>
          ) : includeSuggestions.length === 0 ? (
            <span style={{ fontSize: '0.8rem', color: '#999' }}>All selected</span>
          ) : (
            includeSuggestions.slice(0, 20).map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => addIncludeTagToGroup(activeIndex, tag)}
                style={{
                  padding: '0.2rem 0.4rem',
                  fontSize: '0.8rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {tag}
              </button>
            ))
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', minWidth: 0, maxWidth: '320px', flex: '1 1 280px' }}>
        <span style={{ fontSize: '0.85rem', color: '#555', fontWeight: 'bold' }}>Exclude tags / metrics</span>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '0.35rem',
            padding: '0.4rem',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            background: '#fff',
          }}
        >
          {excludeTags.map((tag) => (
            <span
              key={tag}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.2rem',
                padding: '0.2rem 0.4rem',
                fontSize: '0.85rem',
                background: '#f8d7da',
                border: '1px solid #f1aeb5',
                borderRadius: '4px',
              }}
            >
              {tag}
              <button
                type="button"
                onClick={() => removeExcludeTag(tag)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  lineHeight: 1,
                  color: '#666',
                }}
                aria-label={`Remove ${tag}`}
              >
                ×
              </button>
            </span>
          ))}
          <input
            type="text"
            value={excludeTagInput}
            onChange={(e) => setExcludeTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                addExcludeTag(excludeTagInput);
              }
            }}
            placeholder="Add tag or metric…"
            style={{
              border: 'none',
              outline: 'none',
              padding: '0.2rem',
              fontSize: '0.9rem',
              minWidth: '120px',
              flex: '1 1 120px',
            }}
          />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.2rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: '#666', marginRight: '0.3rem' }}>Suggestions:</span>
          {suggestedTags.length === 0 ? (
            <span style={{ fontSize: '0.8rem', color: '#999' }}>{emptySuggestionsLabel}</span>
          ) : excludeSuggestions.length === 0 ? (
            <span style={{ fontSize: '0.8rem', color: '#999' }}>All selected</span>
          ) : (
            excludeSuggestions.slice(0, 20).map((tag) => (
              <button
                key={tag}
                type="button"
                onClick={() => addExcludeTag(tag)}
                style={{
                  padding: '0.2rem 0.4rem',
                  fontSize: '0.8rem',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                {tag}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default NeraTagFilterRow;
